import React, { useState } from 'react';
import { ReactSVG } from 'react-svg';
import dynamicfunction from '../../utils/dynamicfunction';
import Text from '../Text';
import { fontSizes } from '../../constants/constants';
import ViewReverse from '../ViewReverse';
import { cn } from '../../utils/utils';
import icons from '../../constants/icons';
import SarSymbol from '../SarSymbol';
import ErrorText from '../ErrorText';
import { useSelector } from 'react-redux';
import { getRejectedFieldData, getSectionRejectedFields, isRequiredEmpty } from '../../utils/functions';

const InputField = ({
  type = 'text',
  value,
  onChange,
  placeholder,
  className,
  inputClasses,
  label,
  labelColor = 'text-primary-light',
  divClasses,
  isRequired = false,
  isDisabled = false,
  isPassword,
  maxLength,
  onPressEnter,
  canEnterSpace = true,
  isSymbol = false,
  isRightSymbol = false,
  symbol,
  props,
  error,
  labelDivClass,
  isLabelHeight = false,
  isArabicAllowed = false,
  remarksMetadata = null,
}) => {
  const { TextWeight, TextAlign } = dynamicfunction();
  const [isSecure, setIsSecure] = useState(true);

  const { rejection_summary, is_rejected, field_codes } = useSelector((state) => state.corporate);

  const arabicRegix = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669]/g;

  const handleChange = (e) => {
    const value = e?.target?.value || '';
    if (value?.trim()?.length === 0 && value?.length > 0) {
      return;
    }
    if (isArabicAllowed) {
      onChange(value);
      return;
    }
    const clenedText = value?.replace(arabicRegix, '');
    onChange(clenedText);
  };

  const rejectedFields = getSectionRejectedFields(rejection_summary, remarksMetadata?.sectionId);
  const rejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, remarksMetadata?.key);
  const isFieldDisabled = isDisabled || (is_rejected && remarksMetadata && (!rejectedField?.remarks || rejectedField?.remarks?.length < 1));

  return (
    <div className={cn('w-full', divClasses)}>
      {
        label && (
          <div data-required-empty={isRequiredEmpty(isRequired, label, value)} className={cn(isLabelHeight ? 'md:h-9' : "h-6", labelDivClass)} >
            <Text
              text={label}
              size={fontSizes.h14}
              color={labelColor}
              className={cn('leading-4', isLabelHeight && 'mb-1')}
              isRequired={isRequired}
              isTruncate={false}
            />
          </div>
        )
      }
      <ViewReverse
        className={cn(
          'px-3 border border-grey rounded w-full h-[40px] items-center',
          className,
          isFieldDisabled && 'cursor-not-allowed bg-disabled',
          rejectedField?.remarks?.length > 0 && 'border-red-600',
        )}
      >
        {isSymbol &&
          (symbol ? (
            <Text text={symbol} size={fontSizes.h10} color={isFieldDisabled ? 'text-dark-grey/50' : 'text-black'} isTruncate={false} />
          ) : (
            <SarSymbol color={isFieldDisabled ? 'text-dark-grey/50' : 'text-black'} />
          ))}
        <input
          type={isPassword ? (isSecure ? 'password' : 'text') : type}
          value={value}
          onChange={handleChange}
          disabled={isFieldDisabled}
          placeholder={placeholder}
          className={cn(
            TextWeight.regular,
            TextAlign,
            'outline-none w-full text-sm bg-transparent h-[40px]',
            isFieldDisabled && !value ? 'text-dark-grey/50 cursor-not-allowed' : 'text-black',
            inputClasses,
            rejectedField?.remarks?.length > 0 && 'text-red-600',
          )}
          maxLength={maxLength}
          onKeyDown={(e) => {
            if (e.key === ' ' && !canEnterSpace) {
              e.preventDefault();
              return;
            }
            e.key === 'Enter' && onPressEnter && onPressEnter();
          }}
          onInput={(e) => {
            if (!canEnterSpace) {
              const value = e.target.value;
              if (value.endsWith('. ')) {
                e.target.value = value.slice(0, -2);
              }
            }
          }}
          {...props}
        />
        {isRightSymbol &&
          (symbol ? (
            <Text text={symbol} size={fontSizes.h10} color={isFieldDisabled ? 'text-dark-grey/50' : 'text-black'} isTruncate={false} />
          ) : (
            <SarSymbol color={isFieldDisabled ? 'text-dark-grey/50' : 'text-black'} />
          ))}
        {isPassword && (
          <>
            <ReactSVG
              src={isSecure ? icons.hidePassword : icons.showPassword}
              className='cursor-pointer'
              onClick={() => !isFieldDisabled && setIsSecure(!isSecure)}
            />
          </>
        )}
      </ViewReverse>
      {error?.length > 0 && <ErrorText text={error} />}
      {
        rejectedField?.remarks?.length > 0 && (
          <ViewReverse className='items-center gap-2 mt-2'>
            <ReactSVG src={icons.field_info_outline} />
            <ErrorText text={rejectedField?.remarks} className={'mt-0'} />
          </ViewReverse>
        )
      }
    </div>
  );
};

export default InputField;
